import type { CadDocument, Layer } from '../model/document';
import {
  entityBox,
  transformBlockChild,
  type BlockDef,
  type CadEntity,
} from '../model/entity';
import { boxExpand, boxUnion, flattenArc, type Box } from '../model/geom';
import { radToDeg } from '../model/units';

/**
 * SVG 내보내기 (작업지시서 P3-09)
 *
 * SVG 는 PDF 와 달리 **한글이 그대로 나온다** (font-family 만 지정하면 된다).
 * 도면 검토 공유나 웹 삽입에 쓰기 좋아 PDF 보다 먼저 쓰이는 경우가 많다.
 *
 * 좌표계: SVG 는 Y축이 아래로 증가하므로 전체를 뒤집는다.
 * 1 SVG 단위 = 1 mm 로 두고 width/height 에 mm 단위를 명시해
 * 브라우저·일러스트레이터에서 실제 치수로 열리게 한다.
 */

export interface SvgExportOptions {
  /** 여백 (mm) */
  marginMm?: number;
  /** 배경색. null 이면 투명 */
  background?: string | null;
  /** 숨김 레이어도 포함할지 */
  includeHidden?: boolean;
  /** 지정한 엔티티만 (선택 영역 내보내기) */
  onlyIds?: Set<string>;
}

export function exportSvg(doc: CadDocument, opt: SvgExportOptions = {}): string {
  const margin = opt.marginMm ?? 10;
  const blocks = new Map(doc.blocks.map((b) => [b.code, b]));
  const layers = new Map(doc.layers.map((l) => [l.code, l]));

  const visible = doc.entities.filter((e) => {
    if (opt.onlyIds && !opt.onlyIds.has(e.id)) return false;
    if (opt.includeHidden) return true;
    const l = layers.get(e.layer);
    return l ? l.visible : true;
  });

  const box = extentOf(visible, blocks);
  const marginUm = margin * 1000;
  const padded = boxExpand(box, marginUm);

  const wMm = (padded.maxX - padded.minX) / 1000;
  const hMm = (padded.maxY - padded.minY) / 1000;

  // µm → mm, Y 뒤집기
  const X = (x: number) => (x - padded.minX) / 1000;
  const Y = (y: number) => (padded.maxY - y) / 1000;

  const parts: string[] = [];
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" version="1.1" ` +
      `width="${fmt(wMm)}mm" height="${fmt(hMm)}mm" ` +
      `viewBox="0 0 ${fmt(wMm)} ${fmt(hMm)}">`
  );
  // 뷰어가 물리 치수로 열 수 있도록 단위를 문서에 남긴다
  parts.push(`<desc>StoreCAD export · unit=mm</desc>`);

  if (opt.background) {
    parts.push(`<rect width="100%" height="100%" fill="${esc(opt.background)}"/>`);
  }

  // 레이어 순서대로 그린다. SVG 그룹으로 묶으면 일러스트레이터에서 레이어로 보인다.
  const byLayer = new Map<string, CadEntity[]>();
  for (const e of visible) {
    const arr = byLayer.get(e.layer);
    if (arr) arr.push(e);
    else byLayer.set(e.layer, [e]);
  }

  const ordered = [...byLayer.entries()].sort(
    (a, b) => (layers.get(a[0])?.sortOrder ?? 999) - (layers.get(b[0])?.sortOrder ?? 999)
  );

  for (const [code, list] of ordered) {
    const layer = layers.get(code);
    parts.push(`<g id="${esc(code)}" data-layer="${esc(code)}">`);
    for (const e of list) {
      parts.push(entityToSvg(e, layer, blocks, X, Y));
    }
    parts.push('</g>');
  }

  parts.push('</svg>');
  return parts.join('\n');
}

/* -------------------------------------------------------------------------- *
 * 엔티티 → SVG
 * -------------------------------------------------------------------------- */

function entityToSvg(
  e: CadEntity,
  layer: Layer | undefined,
  blocks: Map<string, BlockDef>,
  X: (n: number) => number,
  Y: (n: number) => number
): string {
  const stroke = e.color ?? layer?.color ?? '#000000';
  // lineWeight 는 1/100 mm → mm
  const w = (e.lineWeight ?? layer?.lineWeight ?? 25) / 100;
  const style = `fill="none" stroke="${esc(stroke)}" stroke-width="${fmt(w)}"`;

  switch (e.type) {
    case 'line':
      return `<line x1="${fmt(X(e.a.x))}" y1="${fmt(Y(e.a.y))}" x2="${fmt(X(e.b.x))}" y2="${fmt(Y(e.b.y))}" ${style}/>`;

    case 'polyline': {
      const pts = e.points.map((p) => `${fmt(X(p.x))},${fmt(Y(p.y))}`).join(' ');
      const tag = e.closed ? 'polygon' : 'polyline';
      return `<${tag} points="${pts}" ${style}/>`;
    }

    case 'circle':
      return `<circle cx="${fmt(X(e.center.x))}" cy="${fmt(Y(e.center.y))}" r="${fmt(e.radius / 1000)}" ${style}/>`;

    case 'arc': {
      // SVG 의 호 명령은 Y 뒤집기와 함께 쓰면 헷갈린다.
      // 선분 근사로 내보낸다 — 화면·인쇄 해상도에서 육안 차이가 없다.
      const pts = flattenArc(e.center, e.radius, e.startAngle, e.endAngle, Math.max(50, e.radius / 60))
        .map((p) => `${fmt(X(p.x))},${fmt(Y(p.y))}`)
        .join(' ');
      return `<polyline points="${pts}" ${style}/>`;
    }

    case 'text': {
      const anchor = e.align === 'center' ? 'middle' : e.align === 'right' ? 'end' : 'start';
      const x = fmt(X(e.at.x));
      const y = fmt(Y(e.at.y));
      const rot = e.rotation !== 0 ? ` transform="rotate(${fmt(-radToDeg(e.rotation))} ${x} ${y})"` : '';
      return (
        `<text x="${x}" y="${y}" font-size="${fmt(e.height / 1000)}" ` +
        `font-family="Noto Sans KR, sans-serif" text-anchor="${anchor}" ` +
        `fill="${esc(stroke)}"${rot}>${esc(e.text)}</text>`
      );
    }

    case 'blockref': {
      const def = blocks.get(e.blockCode);
      if (!def) return `<!-- 블록 정의 없음: ${esc(e.blockCode)} -->`;
      const inner = def.entities
        .map((c) => entityToSvg(transformBlockChild(c, e), layer, blocks, X, Y))
        .join('');
      return `<g data-block="${esc(e.blockCode)}">${inner}</g>`;
    }

    case 'dimension': {
      const seg = (a: { x: number; y: number }, b: { x: number; y: number }) =>
        `<line x1="${fmt(X(a.x))}" y1="${fmt(Y(a.y))}" x2="${fmt(X(b.x))}" y2="${fmt(Y(b.y))}" ${style}/>`;
      const shifted = {
        x: e.offsetPoint.x + (e.b.x - e.a.x),
        y: e.offsetPoint.y + (e.b.y - e.a.y),
      };
      return seg(e.a, e.offsetPoint) + seg(e.b, shifted) + seg(e.offsetPoint, shifted);
    }
  }
}

/* -------------------------------------------------------------------------- *
 * 보조
 * -------------------------------------------------------------------------- */

function extentOf(entities: CadEntity[], blocks: Map<string, BlockDef>): Box {
  if (entities.length === 0) return { minX: 0, minY: 0, maxX: 1000, maxY: 1000 };
  let box: Box | null = null;
  for (const e of entities) {
    const b = entityBox(e, blocks);
    box = box ? boxUnion(box, b) : b;
  }
  return box ?? { minX: 0, minY: 0, maxX: 1000, maxY: 1000 };
}

/** SVG 는 소수 자릿수가 늘면 파일이 급격히 커진다. 3자리(= 1µm)면 충분하다. */
function fmt(v: number): string {
  if (!Number.isFinite(v)) return '0';
  const s = v.toFixed(3);
  return s.replace(/\.?0+$/, '') || '0';
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
